import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReqResService } from 'src/app/service/req-res.service';
import * as Chart from 'chart.js';
import * as XLSX from 'xlsx';

interface ReportItemChartRow {
  itemId: number;
  itemCode: string;
  itemName: string;
  groupName: string;
  segment: string;
  subSegment: string;
  ssubSegment: string;
  totalQty: number;
  totalPrice: number;
}

interface ChartItemSummary {
  key: string;
  itemCode: string;
  itemName: string;
  totalQty: number;
  totalPrice: number;
}

@Component({
  selector: 'app-reportitemchart',
  templateUrl: './reportitemchart.component.html',
  styleUrls: ['./reportitemchart.component.css']
})
export class ReportitemchartComponent implements OnInit, OnDestroy {
  data: ReportItemChartRow[] = [];
  filteredData: ReportItemChartRow[] = [];
  chartItems: ChartItemSummary[] = [];
  chartRows: ReportItemChartRow[] = [];
  statusObj: any[] = [];

  beginDate: string = '';
  endDate: string = '';
  selectedStatus: number = 0;
  selectedMode: 'top' | 'bad' = 'top';
  tableScope: 'chart' | 'all' = 'chart';
  topCount: number = 15;
  isLoading: boolean = false;
  currentView: 'segment' | 'subsegment' = 'segment';
  selectedSegment: string | null = null;

  itemChart: any = null;

  bluePalette: string[] = [
    '#1e3a8a',
    '#1d4ed8',
    '#2563eb',
    '#3b82f6',
    '#0284c7',
    '#0369a1',
    '#0ea5e9',
    '#38bdf8',
    '#4f46e5',
    '#4338ca',
    '#6366f1',
    '#818cf8'
  ];

  constructor(private reqRes: ReqResService) { }

  ngOnInit(): void {
    this.setDefaultDates();
    this.fillStatusDropdown();
    this.getChartData();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  setDefaultDates(): void {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    this.endDate = this.formatDateForInput(today);
    this.beginDate = this.formatDateForInput(oneMonthAgo);
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return year + '-' + month + '-' + day;
  }

  fillStatusDropdown(): void {
    this.reqRes.getData('/Report/GetSpecode/ORDER_STATUS').subscribe(
      (res: any) => {
        this.statusObj = Array.isArray(res) ? res : (res && res.data ? res.data : []);
      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  onSearch(): void {
    this.currentView = 'segment';
    this.selectedSegment = null;
    this.getChartData();
  }

  getChartData(): void {
    if (!this.beginDate || !this.endDate) {
      return;
    }

    this.isLoading = true;

    const url = '/Report/GetItemChartData/' + this.beginDate + '/' + this.endDate + '/' + this.selectedStatus + '/' + this.selectedMode;

    this.reqRes.getData(url).subscribe(
      (res: any) => {
        const rows = Array.isArray(res) ? res : (res && res.data ? res.data : []);
        this.data = rows.map((x: any) => ({
          itemId: Number(x.itemId || 0),
          itemCode: x.itemCode || '',
          itemName: x.itemName || '',
          groupName: x.groupName || '',
          segment: x.segment || 'N/A',
          subSegment: x.subSegment || 'N/A',
          ssubSegment: x.ssubSegment || 'N/A',
          totalQty: Number(x.totalQty || 0),
          totalPrice: Number(x.totalPrice || 0)
        }));
        this.prepareChartData();
        this.renderChart();
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Item chart data error:', error);
        this.data = [];
        this.filteredData = [];
        this.chartItems = [];
        this.chartRows = [];
        this.renderChart();
        this.isLoading = false;
      }
    );
  }

  prepareChartData(): void {
    if (this.currentView === 'subsegment' && this.selectedSegment) {
      this.filteredData = this.data.filter(x => x.segment === this.selectedSegment);
    } else {
      this.filteredData = [...this.data];
    }

    this.filteredData.sort((a, b) => {
      if (this.selectedMode === 'bad') {
        return (a.totalQty - b.totalQty) || (a.totalPrice - b.totalPrice) || a.itemName.localeCompare(b.itemName);
      }

      return (b.totalQty - a.totalQty) || (b.totalPrice - a.totalPrice) || a.itemName.localeCompare(b.itemName);
    });

    const summaryMap: { [key: string]: ChartItemSummary } = {};

    this.filteredData.forEach(row => {
      const key = this.getItemKey(row);

      if (!summaryMap[key]) {
        summaryMap[key] = {
          key: key,
          itemCode: row.itemCode,
          itemName: row.itemName,
          totalQty: 0,
          totalPrice: 0
        };
      }

      summaryMap[key].totalQty += Number(row.totalQty || 0);
      summaryMap[key].totalPrice += Number(row.totalPrice || 0);
    });

    this.chartItems = Object.keys(summaryMap)
      .map(key => summaryMap[key])
      .sort((a, b) => {
        if (this.selectedMode === 'bad') {
          return (a.totalQty - b.totalQty) || (a.totalPrice - b.totalPrice) || a.itemName.localeCompare(b.itemName);
        }

        return (b.totalQty - a.totalQty) || (b.totalPrice - a.totalPrice) || a.itemName.localeCompare(b.itemName);
      })
      .slice(0, Number(this.topCount || 15))
      .reverse();

    const selectedKeys = this.chartItems.map(x => x.key);
    this.chartRows = this.filteredData.filter(row => selectedKeys.indexOf(this.getItemKey(row)) !== -1);
  }

  onTopCountChange(): void {
    this.prepareChartData();
    this.renderChart();
  }

  onTableScopeChange(scope: 'chart' | 'all'): void {
    this.tableScope = scope;
  }

  onModeChange(mode: 'top' | 'bad'): void {
    if (this.selectedMode === mode) return;
    this.selectedMode = mode;
    this.currentView = 'segment';
    this.selectedSegment = null;
    this.getChartData();
  }

  backToSegments(): void {
    this.currentView = 'segment';
    this.selectedSegment = null;
    this.prepareChartData();
    this.renderChart();
  }

  drillToSubsegment(segment: string): void {
    if (!segment || segment === 'No Sales') {
      return;
    }

    this.currentView = 'subsegment';
    this.selectedSegment = segment;
    this.prepareChartData();
    this.renderChart();
  }

  destroyChart(): void {
    if (this.itemChart) {
      this.itemChart.destroy();
      this.itemChart = null;
    }
  }

  renderChart(): void {
    this.destroyChart();

    setTimeout(() => {
      const canvas = document.getElementById('itemSalesChart') as HTMLCanvasElement;
      if (!canvas) {
        return;
      }

      this.itemChart = new Chart(canvas, {
        type: 'horizontalBar',
        data: {
          labels: this.chartItems.map(x => this.getShortName(x.itemName || x.itemCode || '-')),
          datasets: this.prepareDatasets()
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onClick: (evt: any, activeElements: any[]) => {
            if (!activeElements || !activeElements.length || this.currentView === 'subsegment') {
              return;
            }

            const clicked = activeElements[0];
            const dataset = this.itemChart && this.itemChart.data
              ? this.itemChart.data.datasets[clicked._datasetIndex]
              : null;

            if (dataset && dataset.label) {
              this.drillToSubsegment(dataset.label);
            }
          },
          hover: {
            mode: 'nearest',
            intersect: true,
            onHover: (e: any, elements: any[]) => {
              const target = e ? e.target : null;
              if (target) {
                target.style.cursor = elements && elements.length && this.currentView === 'segment' ? 'pointer' : 'default';
              }
            }
          },
          legend: {
            display: true,
            position: 'top',
            labels: {
              fontColor: '#334155',
              boxWidth: 14,
              padding: 14
            }
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            backgroundColor: '#0f172a',
            titleFontColor: '#ffffff',
            bodyFontColor: '#ffffff',
            callbacks: {
              title: (tooltipItems: any[]) => {
                const index = tooltipItems[0] ? tooltipItems[0].index : 0;
                const item = this.chartItems[index];
                return item ? item.itemName : '';
              },
              label: (tooltipItem: any, data: any) => {
                const dataset = data.datasets[tooltipItem.datasetIndex || 0];
                const value = Number(dataset.data[tooltipItem.index || 0] || 0);
                return dataset.label + ': ' + Math.round(value);
              },
              footer: (tooltipItems: any[]) => {
                const index = tooltipItems[0] ? tooltipItems[0].index : 0;
                const item = this.chartItems[index];
                return item ? 'Total: ' + Math.round(item.totalQty) : '';
              }
            }
          },
          scales: {
            xAxes: [{
              stacked: true,
              ticks: {
                beginAtZero: true,
                fontColor: '#475569'
              },
              gridLines: {
                color: 'rgba(148, 163, 184, 0.18)',
                drawBorder: false
              },
              scaleLabel: {
                display: true,
                labelString: this.selectedMode === 'bad' ? 'Sold Quantity' : 'Quantity',
                fontColor: '#334155'
              }
            }],
            yAxes: [{
              stacked: true,
              ticks: {
                fontColor: '#334155'
              },
              gridLines: {
                display: false
              }
            }]
          }
        }
      });
    }, 50);
  }

  prepareDatasets(): any[] {
    const groups = this.getGroupLabels();
    const itemKeys = this.chartItems.map(x => x.key);
    const groupField = this.getCurrentGroupingField();

    return groups.map(group => ({
      label: group,
      data: itemKeys.map(itemKey => {
        return this.chartRows
          .filter(row => this.getItemKey(row) === itemKey && (row as any)[groupField] === group)
          .reduce((sum, row) => sum + Number(row.totalQty || 0), 0);
      }),
      backgroundColor: this.colorFromPalette(group),
      borderColor: this.colorFromPalette(group),
      borderWidth: 1,
      barPercentage: 0.72,
      categoryPercentage: 0.72
    }));
  }

  getCurrentGroupingField(): 'segment' | 'subSegment' {
    return this.currentView === 'segment' ? 'segment' : 'subSegment';
  }

  getGroupLabels(): string[] {
    const field = this.getCurrentGroupingField();
    const labels: string[] = [];

    this.chartRows.forEach(row => {
      const value = (row as any)[field] || 'N/A';
      if (labels.indexOf(value) === -1) {
        labels.push(value);
      }
    });

    return labels;
  }

  colorFromPalette(text: string): string {
    let hash = 0;

    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    return this.bluePalette[Math.abs(hash) % this.bluePalette.length];
  }

  getItemKey(row: ReportItemChartRow): string {
    return row.itemCode || String(row.itemId || row.itemName || '');
  }

  getShortName(text: string): string {
    return text.length > 34 ? text.substring(0, 34) + '...' : text;
  }

  getModeTitle(): string {
    return this.selectedMode === 'bad' ? 'Low Selling Analytics' : 'Item Sales Analytics';
  }

  getModeSubtitle(): string {
    return this.selectedMode === 'bad'
      ? 'Least sold and unsold items'
      : 'Top selling items by selected period and status';
  }

  getChartTitle(): string {
    if (this.currentView === 'subsegment') {
      return this.selectedMode === 'bad' ? 'Least Sold Items by Subsegment' : 'Top Items by Subsegment';
    }

    return this.selectedMode === 'bad' ? 'Least Sold Items by Segment' : 'Top Items by Segment';
  }

  getChartSubtitle(): string {
    return 'Showing ' + this.chartItems.length + ' of ' + this.getUniqueItemCount(this.filteredData) + ' items';
  }

  getTableTitle(): string {
    if (this.currentView === 'subsegment') {
      return this.selectedMode === 'bad' ? 'Low Selling by Subsegment' : 'Item Sales by Subsegment';
    }

    return this.selectedMode === 'bad' ? 'Low Selling by Segment' : 'Item Sales by Segment';
  }

  getTotalQty(): number {
    return this.getTableRows().reduce((sum, x) => sum + Number(x.totalQty || 0), 0);
  }

  getTotalPrice(): number {
    return this.getTableRows().reduce((sum, x) => sum + Number(x.totalPrice || 0), 0);
  }

  getUniqueItemCount(rows: ReportItemChartRow[]): number {
    const keys: string[] = [];

    rows.forEach(row => {
      const key = this.getItemKey(row);
      if (keys.indexOf(key) === -1) {
        keys.push(key);
      }
    });

    return keys.length;
  }

  getTableRows(): ReportItemChartRow[] {
    if (this.tableScope === 'all') {
      return this.filteredData;
    }

    const selectedKeys = this.chartItems.map(x => x.key);
    return this.filteredData.filter(row => selectedKeys.indexOf(this.getItemKey(row)) !== -1);
  }

  getChartHeight(): number {
    const count = Math.max(Number(this.topCount || 15), 10);
    return Math.min(1200, Math.max(460, count * 26 + 120));
  }

  exportTableToExcel(): void {
    const tableRows = this.getTableRows();
    const rows = tableRows.map((x, index) => ({
      'No': index + 1,
      'Item Code': x.itemCode,
      'Item Name': x.itemName,
      'Group': x.groupName,
      'Seg. Level 1': x.segment || '',
      'Seg. Level 2': x.subSegment || '',
      'Seg. Level 3': x.ssubSegment || '',
      'Sold Qty': Math.round(Number(x.totalQty || 0)),
      'Amount': Number(x.totalPrice || 0).toFixed(2)
    }));

    rows.push({
      'No': '',
      'Item Code': '',
      'Item Name': 'TOTAL',
      'Group': '',
      'Seg. Level 1': '',
      'Seg. Level 2': '',
      'Seg. Level 3': '',
      'Sold Qty': Math.round(this.getTotalQty()),
      'Amount': this.getTotalPrice().toFixed(2)
    } as any);

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, this.selectedMode === 'bad' ? 'Low Selling' : 'Item Sales');
    XLSX.writeFile(wb, this.selectedMode === 'bad' ? 'low-selling-chart.xlsx' : 'item-sales-chart.xlsx');
  }
}
